import React, { useState, useEffect } from 'react';
import useAuthStore from '../store/authStore';
import Layout from '../components/Layout';
import api, { leaveAPI } from '../services/api';
import './HRAttendanceNew.css';

const ProjectManagerAttendance = () => {
  const user = useAuthStore((state) => state.user);
  const [activeTab, setActiveTab] = useState('attendance');
  const [leaves, setLeaves] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState({});
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [myAttendance, setMyAttendance] = useState(null);
  const [isPunchedIn, setIsPunchedIn] = useState(false);
  const [attendanceHistory, setAttendanceHistory] = useState([]);

  useEffect(() => {
    fetchMyLeaves();
    fetchMyAttendance();
    fetchAttendanceHistory();
  }, []);

  const fetchMyAttendance = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await api.get(`/time/attendance/my?date=${today}`);
      if (res.data.data) {
        setMyAttendance(res.data.data);
        setIsPunchedIn(res.data.data.punchInTime && !res.data.data.punchOutTime);
      }
    } catch (err) {
      console.error('Error fetching my attendance:', err);
    }
  };

  const fetchAttendanceHistory = async () => {
    try {
      const res = await api.get('/attendance/my-history');
      setAttendanceHistory(res.data.data || []);
    } catch (err) {
      console.error('Error fetching attendance history:', err);
    }
  };

  const fetchMyLeaves = async () => {
    try {
      const res = await leaveAPI.getMyLeaves();
      setLeaves(res.data.data || []);
      calculateLeaveBalance(res.data.data || []);
    } catch (err) {
      console.error('Error fetching leaves:', err);
    }
  };

  const calculateLeaveBalance = (leaveData) => {
    const balance = {
      'Sick Leave': { total: 10, used: 0, remaining: 10 },
      'Casual Leave': { total: 12, used: 0, remaining: 12 },
      'Vacation Leave': { total: 15, used: 0, remaining: 15 }
    };

    leaveData
      .filter(l => l.status === 'Approved')
      .forEach(leave => {
        if (balance[leave.leaveType]) {
          balance[leave.leaveType].used += leave.days;
          balance[leave.leaveType].remaining = balance[leave.leaveType].total - balance[leave.leaveType].used;
        }
      });

    setLeaveBalance(balance);
  };

  const handleApplyLeave = async (formData) => {
    try {
      setLoading(true);
      await leaveAPI.applyLeave(formData);
      alert('Leave applied successfully!');
      setShowLeaveModal(false);
      fetchMyLeaves();
    } catch (err) {
      alert(err.response?.data?.message || 'Error applying leave');
    } finally {
      setLoading(false);
    }
  };

  const handlePunchIn = async () => {
    try {
      setLoading(true);
      await api.post('/time/attendance/punch-in');
      alert('Punched in successfully!');
      fetchMyAttendance();
    } catch (err) {
      alert(err.response?.data?.message || 'Error punching in');
    } finally {
      setLoading(false);
    }
  };

  const handlePunchOut = async () => {
    try {
      setLoading(true);
      await api.post('/time/attendance/punch-out');
      alert('Punched out successfully!');
      fetchMyAttendance();
    } catch (err) {
      alert(err.response?.data?.message || 'Error punching out');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="hr-attendance-container">
        <div className="hr-attendance-header">
          <div className="header-left">
            <h1 className="page-title">My Attendance</h1>
            <p className="page-subtitle">Track your attendance and manage leaves</p>
          </div>
          <div className="header-actions">
            {activeTab === 'leave' && (
              <button className="btn-primary" onClick={() => setShowLeaveModal(true)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Apply Leave
              </button>
            )}
          </div>
        </div>

        {/* Removed My Attendance Today card as per request */}


        <div className="tabs-container">
          <button
            className={`tab-btn ${activeTab === 'attendance' ? 'active' : ''}`}
            onClick={() => setActiveTab('attendance')}
          >
            My Attendance
          </button>
          <button
            className={`tab-btn ${activeTab === 'leave' ? 'active' : ''}`}
            onClick={() => setActiveTab('leave')}
          >
            My Leaves
          </button>
        </div>

        {activeTab === 'attendance' && (
          <AttendanceHistoryTab records={attendanceHistory} />
        )}

        {activeTab === 'leave' && (
          <LeaveTab
            leaves={leaves}
            balance={leaveBalance}
          />
        )}

        {showLeaveModal && (
          <ApplyLeaveModal
            onClose={() => setShowLeaveModal(false)}
            onSubmit={handleApplyLeave}
            loading={loading}
          />
        )}
      </div>
    </Layout>
  );
};

const AttendanceHistoryTab = ({ records }) => {
  return (
    <div className="attendance-table-container">
      <div className="leave-table-header">
        <h3 className="leave-table-title">My Attendance History</h3>
      </div>
      <table className="attendance-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Status</th>
            <th>Punch In</th>
            <th>Punch Out</th>
            <th>Work Hours</th>
          </tr>
        </thead>
        <tbody>
          {records.length === 0 ? (
            <tr><td colSpan="5" className="no-data">No attendance records found</td></tr>
          ) : (
            records.map(record => (
              <tr key={record._id}>
                <td>{new Date(record.date).toLocaleDateString()}</td>
                <td>
                  <span className={`status-badge status-${record.status.toLowerCase().replace(' ', '-')}`}>
                    {record.status}
                  </span>
                </td>
                <td>{record.punchInTime ? new Date(record.punchInTime).toLocaleTimeString() : '—'}</td>
                <td>{record.punchOutTime ? new Date(record.punchOutTime).toLocaleTimeString() : '—'}</td>
                <td>{record.workMinutes ? (record.workMinutes / 60).toFixed(1) + 'h' : '—'}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

const LeaveTab = ({ leaves, balance }) => {
  return (
    <div className="leave-container">
      <div className="leave-balance-grid">
        {Object.entries(balance).map(([type, data]) => (
          <div key={type} className="leave-balance-card">
            <h4 className="leave-type">{type}</h4>
            <div className="leave-stats">
              <div className="leave-stat">
                <span className="leave-stat-label">TOTAL</span>
                <span className="leave-stat-value">{data.total}</span>
              </div>
              <div className="leave-stat">
                <span className="leave-stat-label">USED</span>
                <span className="leave-stat-value used">{data.used}</span>
              </div>
              <div className="leave-stat">
                <span className="leave-stat-label">REMAINING</span>
                <span className="leave-stat-value remaining">{data.remaining}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="leave-table-container">
        <div className="leave-table-header">
          <h3 className="leave-table-title">My Leave History</h3>
        </div>
        <table className="leave-table">
          <thead>
            <tr>
              <th>LEAVE TYPE</th>
              <th>START DATE</th>
              <th>END DATE</th>
              <th>DAYS</th>
              <th>REASON</th>
              <th>STATUS</th>
            </tr>
          </thead>
          <tbody>
            {leaves.length === 0 ? (
              <tr><td colSpan="6" className="no-data">No leave records found</td></tr>
            ) : (
              leaves.map(leave => (
                <tr key={leave._id}>
                  <td>{leave.leaveType}</td>
                  <td>{new Date(leave.startDate).toLocaleDateString()}</td>
                  <td>{new Date(leave.endDate).toLocaleDateString()}</td>
                  <td>{leave.days}</td>
                  <td>{leave.reason}</td>
                  <td>
                    <span className={`status-badge status-${leave.status.toLowerCase()}`}>
                      {leave.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ApplyLeaveModal = ({ onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    leaveType: 'Sick Leave',
    startDate: '',
    endDate: '',
    reason: ''
  });

  const calculateDays = () => {
    if (!formData.startDate || !formData.endDate) return 0;
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      alert('End date cannot be before start date');
      return;
    }
    onSubmit(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Apply Leave</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Leave Type *</label>
            <select
              value={formData.leaveType}
              onChange={(e) => setFormData({ ...formData, leaveType: e.target.value })}
              required
            >
              <option value="Sick Leave">Sick Leave</option>
              <option value="Casual Leave">Casual Leave</option>
              <option value="Vacation Leave">Vacation Leave</option>
              <option value="Maternity Leave">Maternity Leave</option>
              <option value="Paternity Leave">Paternity Leave</option>
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Start Date *</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            <div className="form-group">
              <label>End Date *</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                min={formData.startDate || new Date().toISOString().split('T')[0]}
                required
              />
            </div>
          </div>
          {formData.startDate && formData.endDate && (
            <div className="form-info">
              <strong>Total Days:</strong> {calculateDays()} day(s)
            </div>
          )}
          <div className="form-group">
            <label>Reason *</label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              rows="4"
              placeholder="Please provide a reason for your leave request"
              required
              minLength="10"
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Submitting...' : 'Apply Leave'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectManagerAttendance;
